import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { EMPTY } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';

/** Pantallas cuyo tiempo activo se mide. */
type PantallaMedida = 'conciliacion' | 'informes';

/** Cuerpo de `POST /metricas/tiempo-activo`. */
interface TiempoActivoRequest {
    /** Pantalla donde transcurrió el tiempo. */
    pantalla: PantallaMedida;
    /** Segundos acumulados (1..300). */
    segundos: number;
}

/** Sin interacción durante este tiempo (ms) la persona se considera ausente. */
const INACTIVIDAD_MS = 60_000;
/** Cada cuánto (ms) se envía lo acumulado. */
const ENVIO_MS = 60_000;
/** Tope de segundos por petición que acepta el backend. */
const MAX_SEGUNDOS = 300;
/** Eventos que cuentan como interacción. */
const EVENTOS_INTERACCION = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'scroll'] as const;

/**
 * Mide el tiempo activo de la persona en conciliación e informes y lo reporta al backend.
 * @remarks
 * Alimenta los KPI 1 (tiempo de conciliación) y 2 (tiempo del informe mensual) del cliente en
 * Grafana (ADR `docs/adr/2026-10-22-kpis-del-cliente-en-grafana.md`).
 *
 * Cuenta 1 s por tick mientras la pestaña está visible y hubo interacción (puntero, teclado,
 * rueda o scroll) en los últimos 60 s; pasado ese minuto sin interacción la persona se considera
 * ausente y el contador se detiene. Solo cuentan las rutas de `mapear()`; en el resto no se acumula.
 *
 * Envía lo acumulado cada 60 s, al cambiar de pantalla medida y al ocultarse la pestaña. Un envío
 * fallido se descarta sin reintentar: la métrica es agregada y perder unos segundos no la altera,
 * mientras que reintentar arriesga duplicar tiempo.
 *
 * Qué NO hace: el cuerpo lleva solo la pantalla (`conciliacion` o `informes`) y los segundos; no
 * identifica a la persona ni guarda la URL, sus parámetros ni lo que se hace en la pantalla.
 */
@Injectable({ providedIn: 'root' })
export class TiempoActivoService {
    /** Cliente HTTP. */
    private readonly http = inject(HttpClient);
    /** Router, para saber en qué pantalla está el usuario. */
    private readonly router = inject(Router);
    /** Sesión, para no enviar sin token. */
    private readonly auth = inject(AuthService);
    /** Ciclo de vida del servicio, para limpiar timers y listeners. */
    private readonly destroyRef = inject(DestroyRef);
    /** Endpoint de métricas de interfaz. */
    private readonly url = `${environment.apiUrl}/metricas/tiempo-activo`;

    /** Evita arrancar dos veces. */
    private iniciado = false;
    /** Pantalla medida en este momento; `null` si la ruta no cuenta. */
    private pantalla: PantallaMedida | null = null;
    /** Segundos acumulados de `pantalla` aún sin enviar. */
    private segundos = 0;
    /** Marca de tiempo de la última interacción. */
    private ultimaInteraccion = Number.NEGATIVE_INFINITY;

    /**
     * Arranca la medición de tiempo activo; las llamadas posteriores no hacen nada.
     * @remarks
     * Registra los listeners de interacción y de visibilidad, el reloj de 1 s y el envío periódico;
     * todo se limpia al destruirse el servicio. Se invoca una vez desde el componente raíz.
     */
    iniciar(): void {
        if (this.iniciado) return;
        this.iniciado = true;

        this.pantalla = this.mapear(this.router.url);
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
            .subscribe((e) => {
                const nueva = this.mapear(e.urlAfterRedirects);
                if (nueva === this.pantalla) return;
                this.enviar();
                this.pantalla = nueva;
            });

        const alInteractuar = (): void => { this.ultimaInteraccion = Date.now(); };
        // capture: el scroll no burbujea desde los contenedores internos del layout.
        for (const evento of EVENTOS_INTERACCION) {
            document.addEventListener(evento, alInteractuar, { passive: true, capture: true });
        }
        const alCambiarVisibilidad = (): void => {
            if (document.visibilityState === 'hidden') this.enviar();
        };
        document.addEventListener('visibilitychange', alCambiarVisibilidad);

        const tick = setInterval(() => {
            if (
                this.pantalla !== null
                && document.visibilityState === 'visible'
                && Date.now() - this.ultimaInteraccion < INACTIVIDAD_MS
            ) {
                this.segundos++;
            }
        }, 1000);
        const envio = setInterval(() => this.enviar(), ENVIO_MS);

        this.destroyRef.onDestroy(() => {
            clearInterval(tick);
            clearInterval(envio);
            for (const evento of EVENTOS_INTERACCION) {
                document.removeEventListener(evento, alInteractuar, { capture: true });
            }
            document.removeEventListener('visibilitychange', alCambiarVisibilidad);
        });
    }

    /**
     * Traduce la URL del router a la pantalla medida.
     * @remarks
     * `conciliacion` agrupa `/conciliacion/*`, `/ingresos/*`, `/egresos` y `/resumen-bancos`;
     * `informes` es solo `/informes`. Cualquier otra ruta devuelve `null` y no se mide.
     * @param url - URL con o sin query y fragmento.
     * @returns La pantalla, o `null` si la ruta no cuenta.
     */
    private mapear(url: string): PantallaMedida | null {
        const ruta = url.split(/[?#]/)[0];
        if (/^\/(conciliacion|ingresos)(\/|$)/.test(ruta) || ruta === '/egresos' || ruta === '/resumen-bancos') {
            return 'conciliacion';
        }
        return ruta === '/informes' ? 'informes' : null;
    }

    /**
     * Manda lo acumulado de la pantalla actual y reinicia el contador.
     *
     * Un fallo se descarta; no se envía nada con 0 s.
     * @remarks
     * El contador se reinicia antes de la petición, así que un error de red no devuelve los
     * segundos: se pierden en lugar de reenviarse. El máximo por petición es el que acepta el
     * backend (300 s).
     */
    private enviar(): void {
        const pantalla = this.pantalla;
        // shortcut: el exceso sobre 300 s se descarta, solo ocurriría con un timer detenido y luego reanudado.
        const segundos = Math.min(this.segundos, MAX_SEGUNDOS);
        this.segundos = 0;
        // Sin sesión (p. ej. el NavigationEnd del logout) el POST daría 401 y el interceptor mostraría "sesión cerrada".
        if (pantalla === null || segundos < 1 || !this.auth.isLoggedIn()) return;
        const body: TiempoActivoRequest = { pantalla, segundos };
        this.http.post<void>(this.url, body).pipe(catchError(() => EMPTY)).subscribe();
    }
}
